import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { X } from 'lucide-react';
import { notificaAvviso, notificaErrore, messaggioDa } from '../../utils/notifiche';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';

/**
 * La seduta di pacchetto nel riquadro dell'appuntamento (Alex, 6/10/2026,
 * domanda 11, punti 2 e 3; migration 311).
 *
 * Se il paziente ha un pacchetto aperto per una prestazione prenotata - la
 * prestazione della seduta o la voce del pacchetto - il riquadro mostra
 * l'avviso di «Trasforma in visita», «Pacchetto attivo: seduta N di M — si
 * registra con Continua pacchetto», finche' la seduta di quel giorno non e'
 * registrata. Il testo lo manda il server, come la decisione sul pulsante:
 * qui non si conta niente e non si decide niente.
 *
 * «Continua pacchetto» qui compare solo quando il server lo dice: oggi per
 * l'amministrazione, su un appuntamento di un giorno passato (il medico
 * registra la seduta nella visita, il giorno stesso, come prima). Il motivo
 * si scrive, e resta nella traccia. Sotto l'avviso di un giorno passato,
 * quando il pulsante non c'e', il server dice perche'.
 *
 * Un componente a se', montato con una riga nel riquadro dell'Agenda: il
 * riquadro lo stanno ridisegnando (la fase 1 e lo stile del beta), e cosi'
 * si sposta con lui.
 */
const SedutaPacchetto = ({ appuntamento }) => {
    const [righe, setRighe] = useState([]);
    const [inCorso, setInCorso] = useState(false);
    const id = appuntamento?.id;
    const conPaziente = appuntamento?.tipo === 'visita' && !!appuntamento?.paziente_id;

    const leggi = useCallback(async () => {
        if (!id || !conPaziente) { setRighe([]); return; }
        try {
            const r = await api.get(`/agenda/${id}/seduta-pacchetto`);
            setRighe(r.data?.data?.pacchetti || []);
        } catch (e) {
            /* L'avviso e' un aiuto, non il riquadro: se non arriva, il
               riquadro resta com'era. «Trasforma in visita» lo dice comunque. */
            setRighe([]);
        }
    }, [id, conPaziente]);

    useEffect(() => { leggi(); }, [leggi]);

    /* La finestra del motivo (revisione del beta, 6/10: non window.prompt):
       la riga scelta (null = chiusa) e il testo. Un motivo scritto e non
       mandato e' un modulo sporco: chiudere chiede, e la ricarica automatica
       non la porta via (role="dialog"). */
    const [scelta, setScelta] = useState(null);
    const [motivo, setMotivo] = useState('');
    const [versione, setVersione] = useState(0);
    const sporco = useSporco(scelta ? motivo : null, versione);
    const chiedi = useUscitaProtetta(sporco);
    const azzera = () => { setScelta(null); setMotivo(''); setVersione((v) => v + 1); };
    const apri = (riga) => { setScelta(riga); setMotivo(''); setVersione((v) => v + 1); };
    const chiudi = () => { if (chiedi()) azzera(); };

    const registra = async () => {
        try {
            setInCorso(true);
            const r = await api.post(`/agenda/${id}/seduta-pacchetto`, { pacchetto_id: scelta.pacchetto_id, motivo });
            notificaAvviso(r.data?.message || 'Seduta registrata');
            azzera();
            await leggi();
        } catch (e) {
            /* Il rifiuto del server (il motivo troppo corto, la seduta gia'
               registrata) si legge, e il motivo resta scritto. */
            notificaErrore(messaggioDa(e, 'Non riesco a registrare la seduta'));
        } finally {
            setInCorso(false);
        }
    };

    if (!righe.length) return null;
    return (
        <div className="ag-pacchetto">
            {righe.map((r) => (
                <div key={r.pacchetto_id} className="ag-pacchetto-riga" data-pacchetto={r.pacchetto_id}>
                    <span className="ag-pacchetto-avviso">{r.avviso}</span>
                    {r.nota && <span className="ag-pacchetto-nota">{r.nota}</span>}
                    {r.continua && (
                        <button type="button" className="ag-pacchetto-continua" disabled={inCorso}
                                onClick={() => apri(r)}>
                            Continua pacchetto
                        </button>
                    )}
                </div>
            ))}
            {scelta && (
                /* Dentro il riquadro dell'appuntamento: un clic qui non deve
                   arrivare allo sfondo del riquadro, che lo chiuderebbe. */
                <div className="ag-modale-sfondo ag-pacchetto-sfondo"
                     onClick={(e) => { e.stopPropagation(); chiudi(); }}>
                    <div className="ag-modale ag-modale--stretta ag-pacchetto-finestra" role="dialog" aria-modal="true"
                         aria-label="Continua pacchetto" onClick={(e) => e.stopPropagation()}>
                        <div className="ag-modale-testata">
                            <span>Continua pacchetto</span>
                            <button type="button" onClick={chiudi} aria-label="Chiudi"><X size={18} /></button>
                        </div>
                        <div className="ag-modale-corpo">
                            <p className="ag-pacchetto-avviso">{scelta.avviso}</p>
                            <label className="ag-pacchetto-etichetta" htmlFor="ag-pacchetto-motivo">
                                Motivo della seduta registrata dopo il giorno dell&apos;appuntamento
                                (obbligatorio: resta nella traccia)
                            </label>
                            <textarea id="ag-pacchetto-motivo" className="ag-pacchetto-motivo" rows={3} maxLength={500}
                                      value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                            <div className="ag-modale-azioni">
                                <button type="button" onClick={chiudi} disabled={inCorso}>Annulla</button>
                                <button type="button" className="ag-conferma" onClick={registra}
                                        disabled={inCorso || motivo.trim().length === 0}>
                                    Registra la seduta
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SedutaPacchetto;
